import { useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  CheckCircle2,
  MapPin,
  Award,
  Radar,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { ScrollHero } from "@/components/home/ScrollHero";
import { PartnerWall } from "@/components/home/PartnerWall";
import { TrustCarousel } from "@/components/TrustCarousel";
import { Reveal } from "@/components/Reveal";
import { Odometer } from "@/components/Odometer";
import { RevealHeading } from "@/components/RevealHeading";
import { MotionInView } from "@/components/MotionInView";
import { addressText, stats } from "@/content/site";
import { services } from "@/content/services";
import { industries } from "@/content/industries";
import { posts } from "@/content/blog";

const certifications = ["FMCSA Authority", "SmartWay Partner", "HazMat Certified", "ISO 9001"];

const safetyStats = [
  { icon: ShieldCheck, value: "DOT", label: "Fully compliant" },
  { icon: Award, value: "HazMat", label: "Endorsed lanes" },
  { icon: Radar, value: "Real-time", label: "ELD tracking" },
];

const mapCityDots = [
  { name: "Vancouver", lng: -123.1207, lat: 49.2827 },
  { name: "Seattle", lng: -122.3321, lat: 47.6062 },
  { name: "Calgary", lng: -114.0719, lat: 51.0447 },
  { name: "Salt Lake City", lng: -111.891, lat: 40.7608 },
  { name: "Phoenix", lng: -112.074, lat: 33.4484 },
  { name: "Los Angeles", lng: -118.2437, lat: 34.0522 },
  { name: "Denver", lng: -104.9903, lat: 39.7392 },
  { name: "Dallas", lng: -96.797, lat: 32.7767 },
  { name: "Houston", lng: -95.3698, lat: 29.7604 },
  { name: "Kansas City", lng: -94.5786, lat: 39.0997 },
  { name: "Minneapolis", lng: -93.265, lat: 44.9778 },
  { name: "Chicago", lng: -87.6298, lat: 41.8781 },
  { name: "Detroit", lng: -83.0458, lat: 42.3314 },
  { name: "Atlanta", lng: -84.388, lat: 33.749 },
  { name: "Miami", lng: -80.1918, lat: 25.7617 },
  { name: "Toronto", lng: -79.3832, lat: 43.6532 },
  { name: "Ottawa", lng: -75.6972, lat: 45.4215 },
  { name: "Montreal", lng: -73.5673, lat: 45.5017 },
  { name: "New York", lng: -74.006, lat: 40.7128 },
  { name: "Boston", lng: -71.0589, lat: 42.3601 },
];

const mapCoverageHub = { name: "Aurora", lng: -88.3201, lat: 41.7606 };

// The basemap is an equirectangular (Plate Carrée) crop of NASA's Black Marble
// night-lights mosaic (hue-mapped green), so markers place with a simple linear
// lon/lat mapping. These bounds MUST match the crop in the regen command below.
const MAP_BOUNDS = { lonMin: -130, lonMax: -62, latMin: 22, latMax: 56 };
const MAP_IMAGE = { width: 900, height: 450 }; // 2:1, same ratio as the crop

function mapImagePoint({ lng, lat }: { lng: number; lat: number }) {
  return {
    x: ((lng - MAP_BOUNDS.lonMin) / (MAP_BOUNDS.lonMax - MAP_BOUNDS.lonMin)) * MAP_IMAGE.width,
    y: ((MAP_BOUNDS.latMax - lat) / (MAP_BOUNDS.latMax - MAP_BOUNDS.latMin)) * MAP_IMAGE.height,
  };
}

type MapPoint = { x: number; y: number };

const mapCityDotPixels = mapCityDots.map((city) => ({ ...city, ...mapImagePoint(city) }));
const mapCoverageHubPixel = mapImagePoint(mapCoverageHub);
const cityByName: Record<string, MapPoint> = Object.fromEntries(
  mapCityDotPixels.map((c) => [c.name, { x: c.x, y: c.y }]),
);

// Quadratic arc between two points; `lift` bows it upward like a flight path.
function arcPath(a: MapPoint, b: MapPoint, lift: number) {
  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2 - lift;
  return `M${a.x} ${a.y} Q${midX} ${midY} ${b.x} ${b.y}`;
}

// Primary lanes: Aurora HQ -> every city (interactive; hover lights the nearest).
const mapLanes = mapCityDotPixels.map((city, index) => ({
  name: city.name,
  d: arcPath(mapCoverageHubPixel, city, 22 + Math.abs(city.x - mapCoverageHubPixel.x) * 0.05),
  delay: `-${(index * 0.5).toFixed(2)}s`,
  dur: `${(4.4 + (index % 5) * 0.7).toFixed(1)}s`,
}));

// Ambient inter-city connectors add network density (decorative, no hover).
const mapAmbientLanes = (
  [
    ["Seattle", "Vancouver"],
    ["Los Angeles", "Phoenix"],
    ["Los Angeles", "Dallas"],
    ["Dallas", "Atlanta"],
    ["Atlanta", "Miami"],
    ["Atlanta", "New York"],
    ["Denver", "Kansas City"],
    ["Minneapolis", "Detroit"],
    ["Toronto", "Boston"],
    ["New York", "Boston"],
    ["Houston", "Dallas"],
    ["Salt Lake City", "Denver"],
  ] as const
)
  .filter(([a, b]) => cityByName[a] && cityByName[b])
  .map(([a, b], index) => ({
    key: `${a}-${b}`,
    d: arcPath(cityByName[a], cityByName[b], 14),
    delay: `-${(index * 0.8).toFixed(2)}s`,
  }));

// Pre-rendered NASA Black Marble night-lights crop, hue-mapped to green
// (public domain — no attribution required), framed to the coverage area and
// served as a static asset (no map API calls or tokens). To regenerate (only
// if MAP_BOUNDS changes), download the 13500x6750 source and crop to bounds:
//   curl -sL -o /tmp/bm.jpg "https://eoimages.gsfc.nasa.gov/images/imagerecords/79000/79765/dnb_land_ocean_ice.2012.13500x6750.jpg"
//   magick /tmp/bm.jpg -crop 2550x1275+1875+1275 +repage -resize 2000x1000 -modulate 120 +level-colors "#01140c","#a8ffca" -quality 85 public/brand/coverage-map-night.jpg
const coverageMapUrl = "/brand/coverage-map-night.jpg";

export default function Home() {
  const coverageMapRef = useRef<SVGSVGElement>(null);
  const [activeCoverageCity, setActiveCoverageCity] = useState<string | null>(null);

  function onCoverageMouseMove(event: MouseEvent<SVGSVGElement>) {
    const svg = coverageMapRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scale = Math.max(rect.width / MAP_IMAGE.width, rect.height / MAP_IMAGE.height);
    const offsetX = (rect.width - MAP_IMAGE.width * scale) / 2;
    const offsetY = (rect.height - MAP_IMAGE.height * scale) / 2;
    const x = (event.clientX - rect.left - offsetX) / scale;
    const y = (event.clientY - rect.top - offsetY) / scale;

    let nearest = mapCityDotPixels[0];
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const city of mapCityDotPixels) {
      const distance = Math.hypot(city.x - x, city.y - y);
      if (distance < nearestDistance) {
        nearest = city;
        nearestDistance = distance;
      }
    }
    setActiveCoverageCity(nearestDistance < 90 ? nearest.name : null);
  }

  return (
    <>
      <Seo
        title="Bull & Bear Co - Tanker Trucking Company | Aurora, IL"
        description="Midwest-based tanker carrier hauling liquid bulk freight across all 48 states and into Canada - modern equipment, live ELD tracking, and dispatchers who actually answer."
      />

      {/* HERO - scroll-scrubbed video ad (desktop) / autoplay loop (mobile) */}
      <ScrollHero />

      {/* TRUST STRIP */}
      <section className="hidden border-t border-white/10 bg-brand-800 lg:block">
        <div className="mx-auto max-w-[1280px] px-6 py-3">
          <TrustCarousel />
        </div>
      </section>

      {/* PARTNER LOGO WALL */}
      <PartnerWall />

      {/* SERVICES & INDUSTRIES */}
      <section className="bg-brand-50 py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Services</div>
              <RevealHeading text="What we haul." className="text-4xl lg:text-5xl" />
            </div>
            <Link
              to="/services"
              className={cn(buttonVariants({ variant: "outline" }), "border-brand-900 text-brand-900 hover:bg-brand-900 hover:text-white")}
            >
              View all services <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {services.slice(0, 6).map((s, i) => {
              const Icon = s.icon;
              return (
                <MotionInView key={s.slug} delay={(i % 3) * 0.1} className="h-full">
                  <Link
                    to={`/services/${s.slug}`}
                    className="group block h-full rounded-lg border border-brand-100 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-accent-500 hover:shadow-lg"
                  >
                    <div className="mb-4 flex items-start justify-between">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-brand-900 text-white transition-colors group-hover:bg-accent-500">
                        <Icon className="h-5 w-5" />
                      </div>
                      <ArrowRight className="h-5 w-5 text-brand-300 transition-all group-hover:translate-x-1 group-hover:text-accent-500" />
                    </div>
                    <h3 className="mb-2 text-lg">{s.title}</h3>
                    <p className="text-sm text-brand-500">{s.summary}</p>
                  </Link>
                </MotionInView>
              );
            })}
          </div>

          {/* Industries we serve (merged into the Services section) */}
          <Reveal className="mb-10 mt-20 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Industries</div>
              <RevealHeading text="Specialized lanes, specialized teams." className="text-4xl lg:text-5xl" />
            </div>
            <Link
              to="/industries"
              className={cn(buttonVariants({ variant: "outline" }), "border-brand-900 text-brand-900 hover:bg-brand-900 hover:text-white")}
            >
              View all industries <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <MotionInView key={ind.name} delay={(i % 5) * 0.08}>
                  <Link
                    to={`/industries#${ind.slug}`}
                    className="group relative block aspect-[4/3] overflow-hidden rounded-lg border border-white/5 bg-brand-900 transition-shadow hover:shadow-xl hover:shadow-brand-900/20 focus-visible:ring-2 focus-visible:ring-accent-500"
                  >
                    {ind.image ? (
                      <>
                        <img
                          src={ind.image}
                          alt=""
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {/* Legibility scrim over the photo */}
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-900/90 via-brand-900/30 to-brand-900/10" />
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-tr from-brand-900 via-brand-800 to-brand-700" />
                        <div className="absolute inset-0 bg-gradient-to-tr from-accent-500/25 via-transparent to-transparent" />
                        <div
                          className="absolute inset-0 opacity-[0.07]"
                          style={{
                            backgroundImage:
                              "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 14px)",
                          }}
                        />
                        <Icon
                          className="absolute -bottom-4 -right-4 h-40 w-40 text-accent-400/70 transition-transform duration-500 group-hover:scale-110"
                          strokeWidth={1.75}
                        />
                      </>
                    )}
                    <div className="absolute inset-0 flex flex-col justify-between p-5">
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-white/10 backdrop-blur">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 text-lg font-bold leading-tight text-white lg:text-base">
                          {ind.name}
                          <ArrowRight className="h-4 w-4 shrink-0 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                        </div>
                        <div className="mt-1 hidden max-w-[85%] text-xs text-brand-300 sm:block lg:hidden">{ind.blurb}</div>
                      </div>
                    </div>
                  </Link>
                </MotionInView>
              );
            })}
          </div>
        </div>
      </section>

      {/* COVERAGE MAP — North America night-lights + live freight network */}
      <section className="relative overflow-hidden bg-[#020a07] py-20 text-white lg:py-28">
        <img
          src={coverageMapUrl}
          alt=""
          loading="lazy"
          className="absolute inset-y-0 left-0 h-full w-[115%] max-w-none translate-x-[4%] object-cover object-center"
        />

        {/* Faint ops grid + top atmosphere glow + legibility scrims */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,#7dffa6 0 1px,transparent 1px 40px),repeating-linear-gradient(90deg,#7dffa6 0 1px,transparent 1px 40px)",
          }}
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#7dffa6]/10 to-transparent" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#020a07] via-[#020a07]/80 via-34% to-[#020a07]/10 to-78%" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_45%,transparent_55%,#020a07_100%)]" />

        <svg
          ref={coverageMapRef}
          viewBox={`0 0 ${MAP_IMAGE.width} ${MAP_IMAGE.height}`}
          preserveAspectRatio="xMidYMid slice"
          className="absolute inset-y-0 left-0 h-full w-[115%] translate-x-[4%]"
          onMouseMove={onCoverageMouseMove}
          onMouseLeave={() => setActiveCoverageCity(null)}
          aria-hidden="true"
        >
          <defs>
            <filter id="coverage-glow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="1.6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="coverage-dot-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#5dffa0" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#5dffa0" stopOpacity="0" />
            </radialGradient>
          </defs>

          <rect width={MAP_IMAGE.width} height={MAP_IMAGE.height} fill="transparent" pointerEvents="all" />

          {/* Ambient inter-city connectors (decorative network texture) */}
          <g pointerEvents="none">
            {mapAmbientLanes.map((lane) => (
              <path key={lane.key} d={lane.d} fill="none" stroke="#2bd66a" strokeWidth="0.4" opacity="0.18" />
            ))}
          </g>

          {/* Primary lanes from Aurora HQ, each with a traveling freight pulse */}
          <g pointerEvents="none">
            {mapLanes.map((lane) => {
              const isActive = activeCoverageCity === lane.name;
              return (
                <g key={lane.name}>
                  <path
                    d={lane.d}
                    fill="none"
                    stroke={isActive ? "#7dffa6" : "#2bd66a"}
                    strokeLinecap="round"
                    strokeWidth={isActive ? 1.6 : 0.6}
                    opacity={isActive ? 0.95 : 0.28}
                    filter={isActive ? "url(#coverage-glow)" : undefined}
                  />
                  <circle r={isActive ? 2.4 : 1.5} fill={isActive ? "#ffffff" : "#7dffa6"} opacity={isActive ? 0.95 : 0.7}>
                    <animateMotion dur={isActive ? "2.2s" : lane.dur} begin={lane.delay} repeatCount="indefinite" path={lane.d} />
                  </circle>
                </g>
              );
            })}
          </g>

          {/* City nodes: soft halo + glowing core (a few majors get a pulse ring) */}
          {mapCityDotPixels.map((city) => {
            const isActive = activeCoverageCity === city.name;
            const major = ["New York", "Los Angeles", "Houston", "Atlanta", "Seattle", "Miami"].includes(city.name);
            return (
              <g key={city.name} pointerEvents="none">
                <circle cx={city.x} cy={city.y} r={major ? 8 : 6} fill="url(#coverage-dot-glow)" />
                {major && (
                  <circle cx={city.x} cy={city.y} r="3" fill="none" stroke="#7dffa6" strokeWidth="0.6">
                    <animate attributeName="r" values="3;8;3" dur="3.8s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.6;0;0.6" dur="3.8s" repeatCount="indefinite" />
                  </circle>
                )}
                <circle cx={city.x} cy={city.y} r={isActive ? 2.6 : major ? 2 : 1.5} fill={isActive ? "#ffffff" : "#9bffc0"} filter="url(#coverage-glow)" />
              </g>
            );
          })}

          {/* Aurora HQ — the hub every lane radiates from */}
          <g pointerEvents="none">
            <circle cx={mapCoverageHubPixel.x} cy={mapCoverageHubPixel.y} r="12" fill="url(#coverage-dot-glow)">
              <animate attributeName="r" values="9;15;9" dur="3.2s" repeatCount="indefinite" />
            </circle>
            <circle cx={mapCoverageHubPixel.x} cy={mapCoverageHubPixel.y} r="4" fill="none" stroke="#7dffa6" strokeWidth="0.9">
              <animate attributeName="r" values="4;9;4" dur="3.2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0;0.8" dur="3.2s" repeatCount="indefinite" />
            </circle>
            <circle cx={mapCoverageHubPixel.x} cy={mapCoverageHubPixel.y} r="3" fill="#d6ffe6" filter="url(#coverage-glow)" />
          </g>
        </svg>

        <div className="pointer-events-none relative flex min-h-[520px] items-center px-6 sm:px-10 lg:px-14">
          <Reveal className="pointer-events-auto max-w-xl rounded-2xl border border-white/10 bg-[#020a07]/55 p-6 shadow-2xl shadow-black/25 backdrop-blur-[2px]">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">Coverage</div>
            <RevealHeading
              text="Cross-border reach, Midwest gravity."
              className="mb-5 text-4xl text-white lg:text-5xl"
            />
            <p className="mb-6 text-lg text-brand-100">
              Headquartered in Aurora, IL, with active lanes in every contiguous
              state and cross-border into Canada. Dense in the chemical corridor,
              fast across the Sun Belt, and reliable into the Northeast.
            </p>
            <Link to="/contact" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              Talk to a dispatcher
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SAFETY & CERTS */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Safety & compliance</div>
            <RevealHeading text="Tracked, vetted, and audited - every load." className="mb-10 max-w-2xl text-4xl lg:text-5xl" />
          </Reveal>
          <Reveal className="mb-12 grid gap-6 border-y border-brand-100 py-8 sm:grid-cols-3">
            {[
              { v: stats.years, l: "Years moving freight" },
              { v: stats.lanes, l: "States covered" },
              { v: stats.onTime, l: "On-time delivery" },
            ].map((s) => (
              <div key={s.l} className="text-center sm:text-left">
                <div className="text-4xl font-bold text-accent-500 lg:text-5xl">
                  <Odometer value={s.v} />
                </div>
                <div className="mt-1 text-sm text-brand-500">{s.l}</div>
              </div>
            ))}
          </Reveal>
          <div className="mb-12 grid gap-5 sm:grid-cols-3">
            {safetyStats.map((s, i) => (
              <MotionInView key={s.label} delay={i * 0.1} className="h-full">
                <div className="h-full rounded-lg border border-brand-100 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-accent-500/40 hover:shadow-md">
                  <s.icon className="mb-3 h-7 w-7 text-accent-500" />
                  <div className="text-3xl font-bold text-brand-900">
                    <Odometer value={s.value} />
                  </div>
                  <div className="mt-1 text-sm text-brand-500">{s.label}</div>
                </div>
              </MotionInView>
            ))}
          </div>
          <Reveal className="flex flex-wrap items-center gap-8 opacity-60">
            {certifications.map((c) => (
              <div key={c} className="rounded-md border border-brand-200 px-5 py-2 text-sm font-semibold text-brand-700">
                {c}
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* PORTAL TEASER */}
      <section className="relative overflow-hidden bg-gradient-to-br from-accent-700 via-accent-500 to-accent-600 py-20 text-white lg:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.1]"
          style={{
            backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)",
          }}
        />
        <div className="relative mx-auto grid max-w-[1200px] items-center gap-12 px-6 lg:grid-cols-2">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-white/70">Portals</div>
            <h2 className="mb-5 text-4xl text-white lg:text-5xl">Manage everything in your portal.</h2>
            <p className="mb-7 max-w-lg text-lg text-white/85">
              Drivers, owner-operators, brokers, and our office team each get a
              tailored workspace - built for the work, not the org chart.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/app" className={cn(buttonVariants({ size: "lg" }), "w-full justify-center bg-white text-accent-600 hover:bg-brand-50 sm:w-auto")}>
                Sign in
              </Link>
              <Link
                to="/contact"
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "w-full justify-center border-white/50 bg-transparent text-white hover:bg-white/10 hover:text-white sm:w-auto",
                )}
              >
                Request access
              </Link>
            </div>
          </Reveal>
          <ul className="grid gap-3 sm:auto-rows-fr sm:grid-cols-2">
            {[
              "Track shipments end-to-end",
              "View and download documents",
              "Real-time status updates",
              "Submit applications & onboarding",
            ].map((t, i) => (
              <Reveal key={t} delay={i * 70} className="h-full">
                <li className="flex h-full items-center gap-3 rounded-lg bg-white/10 p-4 backdrop-blur">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-white" />
                  <span className="text-white">{t}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* CAREERS / PARTNER */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-[1200px] gap-6 px-6 md:grid-cols-2">
          {[
            { kicker: "Company drivers", title: "We treat drivers like the pros they are.", cta: "See driving jobs", to: "/careers#openings", image: "/brand/careers/drivers.jpg" },
            { kicker: "Owner-operators", title: "Bring your truck. We'll keep it loaded.", cta: "Start onboarding", to: "/apply?type=carrier", image: "/brand/careers/owner-operators.jpg" },
          ].map((c, i) => (
            <Reveal key={c.kicker} delay={i * 100}>
              <div className="group relative aspect-[5/4] overflow-hidden rounded-lg">
                {c.image ? (
                  <img
                    src={c.image}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900 transition-transform duration-700 group-hover:scale-105" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-900 via-brand-900/50 to-brand-900/10" />
                <div className="absolute inset-0 flex flex-col justify-end p-8">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent-400">{c.kicker}</div>
                  <h3 className="mb-5 max-w-md text-2xl text-white lg:text-3xl">{c.title}</h3>
                  <div>
                    <Link to={c.to} className={cn(buttonVariants({ variant: "accent" }), "w-full justify-center sm:w-auto")}>
                      {c.cta} <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LATEST NEWS */}
      <section className="bg-brand-50 py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">From the dock</div>
              <RevealHeading text="Latest from Bull & Bear." className="text-4xl lg:text-5xl" />
            </div>
            <Link
              to="/blog"
              className={cn(buttonVariants({ variant: "outline" }), "border-brand-900 text-brand-900 hover:bg-brand-900 hover:text-white")}
            >
              View all <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-3">
            {posts.map((p, i) => (
              <MotionInView key={p.slug} delay={(i % 3) * 0.1} className="h-full">
                <Link
                  to={`/blog/${p.slug}`}
                  className="group block h-full overflow-hidden rounded-lg border border-brand-100 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-accent-500 hover:shadow-md"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-brand-900">
                    {p.cover ? (
                      <img
                        src={p.cover}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-900 via-brand-800 to-accent-700" />
                    )}
                    <div className="absolute left-3 top-3">
                      <Badge className="bg-white text-brand-900 hover:bg-white">{p.tag}</Badge>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="mb-2 text-xs text-brand-500">{p.date}</div>
                    <h3 className="mb-2 text-base transition-colors group-hover:text-accent-500">{p.title}</h3>
                    <p className="text-sm text-brand-500">{p.excerpt}</p>
                  </div>
                </Link>
              </MotionInView>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-900 py-16">
        <Reveal className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <div>
            <RevealHeading text="Ready to move it right?" className="text-3xl text-white lg:text-4xl" />
            <p className="mt-2 inline-flex items-center gap-2 text-brand-300">
              <MapPin className="h-4 w-4" /> {addressText}
            </p>
          </div>
          <div className="flex w-full flex-wrap gap-3 md:w-auto">
            <Link to="/contact" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              Get a quote
            </Link>
            <Link
              to="/track"
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "w-full justify-center border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white sm:w-auto",
              )}
            >
              Track shipment
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
